// Hazard-report help is built from the same complete NC development fixture as
// the portal preview. The article deliberately distinguishes fixture values
// from a live site lookup.
import type { HelpArticle } from "../../types";
import {
  PROJECT_HELP_HAZARD,
  PROJECT_HELP_PROJECT,
  PROJECT_HELP_SITE,
} from "../../../project/projectPreviewFixtures";

const windPeriods = PROJECT_HELP_HAZARD.wind.speeds
  .map(({ period, mph }) => `${period} ${mph} mph`)
  .join(" · ");

export const hazardReportsArticle: HelpArticle = {
  id: "hazard-reports",
  title: "Hazard reports & site loads",
  description:
    "Generate the ASCE 7-22 site report, choose the governing wind period, edit deliberately, and recover from complete or partial lookup failures.",
  tags: ["hazards", "ASCE 7-22", "wind", "snow", "seismic", "site loads"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: "The hazard report turns one confirmed map point plus two engineering choices into the project's site-load record. The lookup can return a complete report, a partial report, or no report. Firmark preserves those differences: generated values stay locked by default, missing hazards are flagged rather than invented, and a missing governing wind speed blocks confirmation.",
    },

    { kind: "heading", level: 2, text: "Generate the report", id: "generate" },
    { kind: "where", path: ["Design projects", PROJECT_HELP_PROJECT.name, "Hazard data"] },
    {
      kind: "paragraph",
      text: "Generation is keyed to four inputs: the confirmed latitude and longitude, risk category, site soil class, and ASCE edition. Firmark caches a generated report only while that signature still matches. Move the confirmed point or change one of those engineering choices and the prior report is no longer the report for the current basis.",
    },
    {
      kind: "steps",
      steps: [
        {
          title: "Confirm the site point",
          body: `The Riverside example resolves to ${PROJECT_HELP_SITE.lat}, ${PROJECT_HELP_SITE.lng}. A postal address alone does not provide the point used by the lookup.`,
        },
        {
          title: "Choose risk category and site soil class",
          body: "These are engineer-controlled intake values. The form starts at Risk Category II and Site Class D; review them against the project basis before generating.",
        },
        {
          title: "Generate ASCE 7-22 report",
          body: "The lookup requests the site, wind, snow, seismic, ice, rain, flood, tornado, and tsunami records. A loading view remains cancellable through **Skip & enter values manually**.",
        },
        {
          title: "Review, then confirm",
          body: "The generated report opens on its governing values, with more detail available in each hazard card. **Confirm hazard data** stays disabled while the lookup is running and whenever a failed wind lookup has no manual replacement speed.",
        },
      ],
    },
    {
      kind: "shot",
      shotId: "hazard-report-intake",
      caption: "Hazard intake: the confirmed point is paired with the engineer-selected risk category and soil class.",
    },

    { kind: "heading", level: 2, text: "Read the generated record", id: "read" },
    {
      kind: "paragraph",
      text: "Wind is shown as both a design speed and a return-period series. If the returned series includes the period you need, the **Governing return period** selector changes which speed is promoted as governing without discarding the other periods. The rest of the report remains separated by hazard, so a flood datum is never confused with a seismic parameter or a ground snow load.",
    },
    {
      kind: "values",
      caption: "Deterministic teaching values in the Riverside capture — these are fixture data, not a promise about a live lookup for this address.",
      rows: [
        { label: "Wind", value: `${PROJECT_HELP_HAZARD.wind.designSpeed} mph design · ${windPeriods}` },
        { label: "Ground snow", value: `${PROJECT_HELP_HAZARD.snow.pg} psf · 20-year MRI ${PROJECT_HELP_HAZARD.snow.mri20} psf` },
        { label: "Seismic", value: `Ss ${PROJECT_HELP_HAZARD.seismic.Ss} · S1 ${PROJECT_HELP_HAZARD.seismic.S1} · SDS ${PROJECT_HELP_HAZARD.seismic.SDS} · SDC ${PROJECT_HELP_HAZARD.seismic.sdc}` },
        { label: "Ice", value: `${PROJECT_HELP_HAZARD.ice.thickness} in · ${PROJECT_HELP_HAZARD.ice.temperature} °F · ${PROJECT_HELP_HAZARD.ice.gustSpeed} mph gust` },
        { label: "Rain", value: `${PROJECT_HELP_HAZARD.rain.intensity15min} in/hr at 15 min · ${PROJECT_HELP_HAZARD.rain.intensity60min} in/hr at 60 min` },
        { label: "Flood", value: `Zone ${PROJECT_HELP_HAZARD.flood.zone} · BFE ${PROJECT_HELP_HAZARD.flood.bfe} ft · ${PROJECT_HELP_HAZARD.flood.datum}` },
      ],
    },
    {
      kind: "shot",
      shotId: "hazard-report-wind",
      caption: "The generated wind card: governing speed, return-period series, and the selector that promotes one returned period.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "Generated does not mean immutable",
      body: "Generated values begin locked so an accidental keystroke cannot alter the record. Editing is available, but it is a deliberate mode and it clears a prior setup confirmation.",
    },

    { kind: "heading", level: 2, text: "Edit, regenerate, or start over", id: "editing" },
    {
      kind: "paragraph",
      text: "Use **Edit values** to unlock generated rows. Firmark stores those edits as overrides on the generated report; **Lock** ends editing without deleting them. **Reset all edits** clears the overrides and returns the rows to their generated values. **Regenerate** clears the overrides and requests a fresh report for the current signature. **Start over** returns to intake.",
    },
    {
      kind: "shot",
      shotId: "hazard-report-editing",
      caption: "Editing is explicit: generated rows unlock, reset is visible, and regeneration remains a separate action.",
    },

    { kind: "heading", level: 2, text: "When the lookup is incomplete", id: "failures" },
    {
      kind: "paragraph",
      text: "A complete lookup failure opens manual entry after the automatic attempt; the loading view also offers **Skip & enter values manually**. Manual rows may be left blank, and the product warns that blank values are excluded from design. **Retry lookup** attempts the automatic report again.",
    },
    {
      kind: "paragraph",
      text: "A partial response behaves differently. Successful hazard cards stay intact, failed cards are flagged and their rows become editable, and **Retry lookup** asks only for a fresh report without throwing away already successful values. The merge keeps a prior successful hazard when the same-signature retry fails that hazard again. The list of failures shrinks to hazards still unresolved after the merge.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Wind is the confirmation gate",
      body: "Other failed hazards warn and can remain blank, which excludes them from design. If the wind hazard failed, enter a governing design wind speed or retry successfully before Firmark enables **Confirm hazard data**.",
    },

    { kind: "related", topics: ["project-setup", "floor-plans", "viewer-review"] },
  ],
};
